import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '../AppIcon';
import { cn } from '../../utils/cn';

export type YearMonthPickerProps = {
  date: Date;
  onChange: (d: Date) => void;
  fromMonth?: Date; // default: 8 years back Jan 1
  toMonth?: Date;   // default: next year Dec 31
  isRtl?: boolean; // optional for legacy usage
  variant?: 'outlined' | 'text' | 'plain';
  dense?: boolean; // legacy name
  compact?: boolean; // alias for dense
  className?: string;
  disableYearSelect?: boolean; // hide year dropdown (readonly year)
  disableMonthSelect?: boolean; // hide month dropdown (readonly month)
  showLabel?: boolean; // show combined visual label MMM YYYY
  fullWidth?: boolean; // stretch container
  onMonthChange?: (d: Date) => void; // side effect hook
  onYearChange?: (d: Date) => void; // side effect hook
  scrollBehavior?: 'none' | 'month' | 'year'; // wheel scroll granularity
};

// Utility to clamp date within range
function clamp(date: Date, min: Date, max: Date) {
  if (date < min) return new Date(min);
  if (date > max) return new Date(max);
  return date;
}

const YearMonthPicker: React.FC<YearMonthPickerProps> = ({
  date,
  onChange,
  fromMonth,
  toMonth,
  isRtl = false,
  variant = 'outlined',
  dense = false,
  compact = false,
  className = '',
  disableYearSelect = false,
  disableMonthSelect = false,
  showLabel = false,
  fullWidth = false,
  onMonthChange,
  onYearChange,
  scrollBehavior = 'month'
}) => {
  const { t } = useTranslation(['common', 'translation']);
  const start = fromMonth ?? new Date(new Date().getFullYear() - 8, 0, 1);
  const end = toMonth ?? new Date(new Date().getFullYear() + 1, 11, 31);

  const months = useMemo(() => ([
    t('common:January'), t('common:February'), t('common:March'), t('common:April'), t('common:May'), t('common:June'),
    t('common:July'), t('common:August'), t('common:September'), t('common:October'), t('common:November'), t('common:December')
  ]), [t]);

  const monthAbbrevs = useMemo(() => months.map(m => (m.length > 4 ? m.slice(0,3) : m)), [months]);

  // Build year array inclusive
  const years: number[] = [];
  for (let y = start.getFullYear(); y <= end.getFullYear(); y++) years.push(y);

  const handleSelectChange = (kind: 'month' | 'year', value: number) => {
    const newDate = new Date(date);
    if (kind === 'month') {
      newDate.setMonth(value, 1);
    } else {
      newDate.setFullYear(value, newDate.getMonth(), 1);
    }
    const finalDate = clamp(newDate, start, end);
    onChange(finalDate);
    if (kind === 'month' && onMonthChange) onMonthChange(finalDate);
    if (kind === 'year' && onYearChange) onYearChange(finalDate);
  };

  const go = useCallback((delta: number) => {
    const d = new Date(date);
    d.setMonth(d.getMonth() + delta, 1);
    const finalDate = clamp(d, start, end);
    onChange(finalDate);
    if (delta !== 0 && onMonthChange) onMonthChange(finalDate);
  }, [date, onChange, onMonthChange, start, end]);

  // Compare by absolute month index (year*12 + month)
  const currentIdx = date.getFullYear() * 12 + date.getMonth();
  const startIdx = start.getFullYear() * 12 + start.getMonth();
  const endIdx = end.getFullYear() * 12 + end.getMonth();
  const canPrev = currentIdx > startIdx;
  const canNext = currentIdx < endIdx;

  const isCompact = dense || compact;

  // Keyboard support
  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        go(isRtl ? 1 : -1);
      } else if (e.key === 'ArrowRight') {
        go(isRtl ? -1 : 1);
      } else if (e.key === 'PageUp') {
        const d = new Date(date); d.setFullYear(d.getFullYear() - 1, d.getMonth(), 1); onChange(clamp(d, start, end)); if(onYearChange) onYearChange(d);
      } else if (e.key === 'PageDown') {
        const d = new Date(date); d.setFullYear(d.getFullYear() + 1, d.getMonth(), 1); onChange(clamp(d, start, end)); if(onYearChange) onYearChange(d);
      } else return;
      e.preventDefault();
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [go, date, isRtl, onChange, onYearChange, start, end]);

  // Scroll (wheel) navigation
  useEffect(() => {
    if (scrollBehavior === 'none') return;
    const el = containerRef.current; if(!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      if (scrollBehavior === 'month') {
        go(e.deltaY > 0 ? 1 : -1);
      } else if (scrollBehavior === 'year') {
        const d = new Date(date); d.setFullYear(d.getFullYear() + (e.deltaY > 0 ? 1 : -1)); onChange(clamp(d, start, end)); if(onYearChange) onYearChange(d);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel as any);
  }, [scrollBehavior, go, date, onChange, onYearChange, start, end]);

  const variantStyles: Record<string, string> = {
    outlined: 'border border-border bg-card shadow-sm',
    text: 'bg-transparent',
    plain: 'bg-transparent border border-border'
  };

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label={t('common:Select Month and Year') as string}
      className={cn(
        'flex items-center rounded-md',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary',
        isCompact ? 'gap-2 p-1' : 'gap-3 p-2',
        fullWidth && 'w-full',
        variantStyles[variant] || '',
        className
      )}
      tabIndex={0}
    >
      <button
        className={cn(
          'rounded transition-colors hover:bg-muted disabled:opacity-40',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          isCompact ? 'p-0.5' : 'p-1'
        )}
        disabled={!canPrev}
        onClick={() => go(-1)}
        aria-label={t('common:Previous Month') as string}
        title={t('common:Previous Month') as string}
      >
        <AppIcon name={isRtl ? "ChevronRight" : "ChevronLeft"} size={isCompact ? 16 : 20} />
      </button>

      {/* Selectors */}
      <div className="flex items-center gap-2">
        {!disableMonthSelect && (
          <select
            className={cn(
              'rounded border border-border bg-card px-2 py-1 text-sm text-foreground',
              'focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus-visible:outline-none',
              isCompact ? 'min-w-[90px]' : 'min-w-[130px]'
            )}
            value={date.getMonth()}
            onChange={(e) => handleSelectChange('month', Number(e.target.value))}
            aria-label={t('common:Month') as string}
          >
            {months.map((m, i) => (
              <option key={m} value={i}>{isCompact ? monthAbbrevs[i] : m}</option>
            ))}
          </select>
        )}
        {!disableYearSelect && (
          <select
            className={cn(
              'rounded border border-border bg-card px-2 py-1 text-sm text-foreground',
              'focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus-visible:outline-none',
              isCompact ? 'min-w-[80px]' : 'min-w-[100px]'
            )}
            value={date.getFullYear()}
            onChange={(e) => handleSelectChange('year', Number(e.target.value))}
            aria-label={t('common:Year') as string}
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
        {showLabel && (
          <div className="flex items-center text-sm text-muted-foreground font-medium" aria-hidden>
            {months[date.getMonth()]} {date.getFullYear()}
          </div>
        )}
      </div>

      <button
        className={cn(
          'rounded transition-colors hover:bg-muted disabled:opacity-40',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          isCompact ? 'p-0.5' : 'p-1'
        )}
        disabled={!canNext}
        onClick={() => go(1)}
        aria-label={t('common:Next Month') as string}
        title={t('common:Next Month') as string}
      >
        <AppIcon name={isRtl ? "ChevronLeft" : "ChevronRight"} size={isCompact ? 16 : 20} />
      </button>
    </div>
  );
};

export default YearMonthPicker;
